import React, { useEffect, useRef, useState, useContext, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FiAlertCircle, FiCheck, FiRefreshCw } from "react-icons/fi";
import MarketNavbar from "../MarketNav/MarketNavbar";
import MarketplacePage from "../components/MarketplacePage";
import useVerifyMarketplacePayment from "../libs/useVerifyMarketplacePayment";
import { MARKET } from "../libs/marketplaceRoutes";
import { CartContext } from "../../../Context/CartContext";
import { AuthContext } from "../../../Context/AuthContext";
import { clearServerCart } from "../libs/cartSyncApi";
import { CHECKOUT_COMPLETED_KEY, GUEST_CHECKOUT_EMAIL_KEY } from "../libs/useCheckout";
import RetryPaymentButton from "../components/RetryPaymentButton";
import { canRetryPayment } from "../components/RetryPaymentButton";
import "./PaymentVerification.css";

const VERIFY_STEPS = [
  "Connecting to payment provider",
  "Confirming your transaction",
  "Finalizing your order",
];

const REDIRECT_SECONDS = 3;

const PaymentVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { mutate: verifyPayment, isLoading } = useVerifyMarketplacePayment();
  const { clearAllCart } = useContext(CartContext);
  const { token, isLoggedIn } = useContext(AuthContext);
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("Please wait while we confirm your payment.");
  const [orderInfo, setOrderInfo] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const [showEscapeHatch, setShowEscapeHatch] = useState(false);
  const verifyStartedRef = useRef(false);
  const verifyCompletedRef = useRef(false);

  const orderIdParam = searchParams.get("orderId");
  const reference = useMemo(
    () =>
      searchParams.get("reference") ||
      searchParams.get("trxref") ||
      searchParams.get("transaction_id") ||
      searchParams.get("tx_id"),
    [searchParams]
  );

  const confirmationPath = orderInfo?.orderId
    ? `/market/order-confirmation/${orderInfo.orderId}`
    : orderIdParam
      ? `/market/order-confirmation/${orderIdParam}`
      : null;

  useEffect(() => {
    if (status !== "verifying") return undefined;

    const stepTimer = setInterval(() => {
      setActiveStep((prev) => (prev < VERIFY_STEPS.length - 1 ? prev + 1 : prev));
    }, 2200);

    const escapeTimer = setTimeout(() => setShowEscapeHatch(true), 20000);

    const timeoutTimer = setTimeout(() => {
      if (verifyCompletedRef.current) return;
      setStatus("failed");
      setMessage(
        "Verification is taking longer than expected. You can retry below or check your email for confirmation."
      );
    }, 45000);

    return () => {
      clearInterval(stepTimer);
      clearTimeout(escapeTimer);
      clearTimeout(timeoutTimer);
    };
  }, [status]);

  useEffect(() => {
    if (status !== "success" || !confirmationPath) return undefined;

    setCountdown(REDIRECT_SECONDS);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(confirmationPath, { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const fallback = setTimeout(() => {
      navigate(confirmationPath, { replace: true });
    }, REDIRECT_SECONDS * 1000 + 500);

    return () => {
      clearInterval(timer);
      clearTimeout(fallback);
    };
  }, [status, confirmationPath, navigate]);

  useEffect(() => {
    if (verifyStartedRef.current) return undefined;
    verifyStartedRef.current = true;

    const orderId = orderIdParam;

    if (!reference) {
      setStatus("failed");
      setMessage("Missing payment reference. Return to checkout and try again.");
      return undefined;
    }

    verifyPayment(
      { reference, orderId },
      {
        onSuccess: (data) => {
          verifyCompletedRef.current = true;
          sessionStorage.setItem(CHECKOUT_COMPLETED_KEY, "1");
          clearAllCart();
          if (token) {
            clearServerCart(token).catch(() => {});
          }

          const resolvedOrder = data.order || {};
          if (resolvedOrder.shippingAddress?.email && !token) {
            sessionStorage.setItem(
              GUEST_CHECKOUT_EMAIL_KEY,
              resolvedOrder.shippingAddress.email.trim().toLowerCase()
            );
          }

          const redirectId = resolvedOrder._id || orderId;
          setOrderInfo({
            orderId: redirectId,
            orderNumber: resolvedOrder.orderNumber,
            order: resolvedOrder,
          });
          setStatus("success");
          setMessage(data.message || "Payment successful! Your order is confirmed.");
        },
        onError: (error) => {
          verifyCompletedRef.current = true;
          const apiMessage = error.response?.data?.message;
          setStatus("failed");
          setMessage(
            apiMessage ||
              "Payment verification failed. If you were charged, check your email or retry below."
          );
          if (orderId) {
            setOrderInfo({
              orderId,
              order: { _id: orderId, paymentStatus: "failed", status: "Pending" },
            });
          }
        },
      }
    );

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference, orderIdParam]);

  const handleRetryVerify = () => {
    if (!reference) return;
    verifyCompletedRef.current = false;
    setShowEscapeHatch(false);
    setStatus("verifying");
    setActiveStep(0);
    setMessage("Please wait while we confirm your payment.");
    verifyPayment(
      { reference, orderId: orderIdParam },
      {
        onSuccess: (data) => {
          verifyCompletedRef.current = true;
          sessionStorage.setItem(CHECKOUT_COMPLETED_KEY, "1");
          clearAllCart();
          if (token) {
            clearServerCart(token).catch(() => {});
          }
          const resolvedOrder = data.order || {};
          const redirectId = resolvedOrder._id || orderIdParam;
          setOrderInfo({
            orderId: redirectId,
            orderNumber: resolvedOrder.orderNumber,
            order: resolvedOrder,
          });
          setStatus("success");
          setMessage(data.message || "Payment successful! Your order is confirmed.");
        },
        onError: (error) => {
          verifyCompletedRef.current = true;
          setStatus("failed");
          setMessage(
            error.response?.data?.message ||
              "Payment verification failed. If you were charged, check your email or retry below."
          );
        },
      }
    );
  };

  const retryOrder =
    orderInfo?.order ||
    (orderIdParam ? { _id: orderIdParam, paymentStatus: "failed", status: "Pending" } : null);

  return (
    <>
      <MarketNavbar />
      <MarketplacePage className="marketplace-page--checkout">
        <div className="payment-verify">
          <div className="payment-verify__notice" role="status">
            <FiAlertCircle aria-hidden="true" />
            <span>Please keep this tab open until verification completes.</span>
          </div>

          <div className="payment-verify__card">
            {(status === "verifying" || isLoading) && (
              <>
                <div className="payment-verify__icon-wrap payment-verify__icon-wrap--loading">
                  <div className="payment-verify__spinner" aria-hidden="true" />
                </div>
                <h1 className="payment-verify__title">Verifying your payment</h1>
                <p className="payment-verify__subtitle">{message}</p>
                <div className="payment-verify__steps" aria-live="polite">
                  {VERIFY_STEPS.map((step, index) => {
                    let stepClass = "payment-verify__step";
                    if (index < activeStep) stepClass += " payment-verify__step--done";
                    else if (index === activeStep) stepClass += " payment-verify__step--active";
                    return (
                      <div key={step} className={stepClass}>
                        <span className="payment-verify__step-dot" aria-hidden="true" />
                        <span>{step}{index === activeStep ? "…" : index < activeStep ? " ✓" : ""}</span>
                      </div>
                    );
                  })}
                </div>
                {showEscapeHatch && confirmationPath && (
                  <div className="payment-verify__actions" style={{ marginTop: 20 }}>
                    <p className="payment-verify__subtitle" style={{ marginBottom: 12 }}>
                      Payment may already be complete. You can open your confirmation page while we finish up.
                    </p>
                    <Link to={confirmationPath} className="payment-verify__btn payment-verify__btn--secondary">
                      View order confirmation
                    </Link>
                  </div>
                )}
              </>
            )}

            {status === "success" && !isLoading && (
              <>
                <div className="payment-verify__icon-wrap payment-verify__icon-wrap--success">
                  <FiCheck size={36} aria-hidden="true" />
                </div>
                <h1 className="payment-verify__title">Payment confirmed</h1>
                <p className="payment-verify__subtitle">{message}</p>
                {orderInfo?.orderNumber && (
                  <p className="payment-verify__order-number">
                    Order number: <strong>{orderInfo.orderNumber}</strong>
                  </p>
                )}
                <p className="payment-verify__countdown">
                  Redirecting to your confirmation in <strong>{countdown}</strong>{" "}
                  {countdown === 1 ? "second" : "seconds"}…
                </p>
                <div className="payment-verify__actions">
                  {confirmationPath && (
                    <Link to={confirmationPath} className="payment-verify__btn payment-verify__btn--primary">
                      View order confirmation
                    </Link>
                  )}
                  {orderInfo?.orderNumber && (
                    <Link
                      to={`${MARKET.TRACK_ORDER}?orderNumber=${encodeURIComponent(orderInfo.orderNumber)}`}
                      className="payment-verify__btn payment-verify__btn--secondary"
                    >
                      Track order
                    </Link>
                  )}
                </div>
              </>
            )}

            {status === "failed" && !isLoading && (
              <>
                <div className="payment-verify__icon-wrap payment-verify__icon-wrap--error">
                  <FiAlertCircle size={36} aria-hidden="true" />
                </div>
                <h1 className="payment-verify__title">Verification issue</h1>
                <p className="payment-verify__subtitle">{message}</p>
                <div className="payment-verify__actions">
                  {reference && (
                    <button
                      type="button"
                      className="payment-verify__btn payment-verify__btn--primary"
                      onClick={handleRetryVerify}
                    >
                      <FiRefreshCw aria-hidden="true" /> Retry verification
                    </button>
                  )}
                  {isLoggedIn && retryOrder && canRetryPayment(retryOrder) && (
                    <RetryPaymentButton order={retryOrder} className="payment-verify__btn payment-verify__btn--secondary" />
                  )}
                  {confirmationPath && (
                    <Link to={confirmationPath} className="payment-verify__link">
                      Check order confirmation anyway
                    </Link>
                  )}
                  {orderIdParam && isLoggedIn && (
                    <Link to={`/account/orders/${orderIdParam}`} className="payment-verify__link">
                      View order details
                    </Link>
                  )}
                  {!isLoggedIn && orderIdParam && (
                    <Link
                      to={`/login?returnUrl=${encodeURIComponent(`/account/orders/${orderIdParam}`)}`}
                      className="payment-verify__link"
                    >
                      Log in to retry payment
                    </Link>
                  )}
                  <Link to="/market/cart" className="payment-verify__link">
                    Return to cart
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </MarketplacePage>
    </>
  );
};

export default PaymentVerification;
